import { MedChronExportOptionsFields } from "@/components/MedicalChronology/MedChronExportOptionsFields";
import { Loading } from "@/components/ui";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ExportFormat } from "@/hooks/useExportCase";
import type { MedChronExportUiState } from "@/services/userPreferencesService.types";
import { cn } from "@paribus/shared-ui";
import { X } from "lucide-react";
import { useRef } from "react";

const FORMAT_OPTIONS: { value: ExportFormat; label: string }[] = [
  { value: ExportFormat.PDF, label: "PDF" },
  { value: ExportFormat.DOCX, label: "DOCX" },
];

const FORMAT_GROUP_LABEL_ID = "export-customization-format-label";

export interface ExportCustomizationViewProps {
  caseName?: string | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  formState: MedChronExportUiState | null;
  /** Document format for this one-off export; excluded from "Save Preferences". */
  format: ExportFormat;
  isDirty: boolean;
  isSaving: boolean;
  isExporting: boolean;
  onFieldChange: (patch: Partial<MedChronExportUiState>) => void;
  onFormatChange: (format: ExportFormat) => void;
  onReset: () => void;
  onExport: () => void;
  onSaveAsDefault: () => void;
  onClose: () => void;
}

export const ExportCustomizationView = ({
  caseName,
  isLoading,
  isError,
  errorMessage,
  formState,
  format,
  isDirty,
  isSaving,
  isExporting,
  onFieldChange,
  onFormatChange,
  onReset,
  onExport,
  onSaveAsDefault,
  onClose,
}: ExportCustomizationViewProps) => {
  const isBusy = isSaving || isExporting;
  const formatButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Roving arrow-key selection, which `role="radiogroup"` promises but plain buttons don't
  // give for free. Selection follows focus, matching native radio behaviour.
  const handleFormatKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
    const backward = event.key === "ArrowLeft" || event.key === "ArrowUp";
    if (!forward && !backward) return;

    event.preventDefault();
    const current = FORMAT_OPTIONS.findIndex((option) => option.value === format);
    const next = (current + (forward ? 1 : -1) + FORMAT_OPTIONS.length) % FORMAT_OPTIONS.length;
    onFormatChange(FORMAT_OPTIONS[next].value);
    formatButtonRefs.current[next]?.focus();
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        size="lg"
        hideCloseButton
        className="flex max-h-[95vh] flex-col gap-0 overflow-hidden p-0"
      >
        {/* Header */}
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between space-y-0 border-b border-neutral-100 px-4 py-4 text-left sm:px-6">
          <div>
            <DialogTitle className="text-xl font-bold text-neutral-900">
              Customize Export
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-neutral-500">
              {caseName ? `Appearance and layout for ${caseName}` : "Appearance and layout"}
            </DialogDescription>
          </div>
          <DialogClose
            type="button"
            className="rounded-full p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            aria-label="Close"
          >
            <X size={20} />
          </DialogClose>
        </DialogHeader>

        {/* Body — min-h-0 lets this shrink inside the flex column so the footer is never
            pushed past max-h and clipped; without it the buttons vanish on short viewports. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {isError && (
            <p
              role="alert"
              className="mb-4 rounded-lg bg-destructive-subtle p-4 text-sm font-medium text-destructive-text"
            >
              {errorMessage || "Failed to load export preferences."}
            </p>
          )}

          {(isLoading || !formState) && !isError && (
            <div className="py-10">
              <Loading fullScreen={false} size={32} text="Loading export preferences..." />
            </div>
          )}

          {formState && !isError && (
            <div className="flex flex-col">
              <div className="mb-3">
                <span
                  id={FORMAT_GROUP_LABEL_ID}
                  className="mb-2 block text-sm font-semibold text-muted-foreground"
                >
                  Format
                </span>
                <div
                  role="radiogroup"
                  aria-labelledby={FORMAT_GROUP_LABEL_ID}
                  onKeyDown={handleFormatKeyDown}
                  className="flex gap-2"
                >
                  {FORMAT_OPTIONS.map((option, index) => {
                    const isSelected = format === option.value;
                    return (
                      <button
                        key={option.value}
                        ref={(element) => {
                          formatButtonRefs.current[index] = element;
                        }}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={isSelected ? 0 : -1}
                        onClick={() => onFormatChange(option.value)}
                        className={cn(
                          "min-w-24 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors focus-visible:shadow-focus",
                          isSelected
                            ? "border-primary bg-primary-subtle text-primary-emphasis"
                            : "border-input bg-surface-subtle text-muted-foreground hover:bg-surface-sunken hover:text-foreground"
                        )}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <MedChronExportOptionsFields
                formState={formState}
                onFieldChange={onFieldChange}
                idPrefix="export-customization-"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        {/* Mobile: Save Preferences spans the top row, Reset and Export share the row below.
            `order-*` drives grid placement; at sm: it becomes the plain right-aligned row. */}
        <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-neutral-100 bg-neutral-50 px-4 py-4 sm:flex sm:flex-wrap sm:items-center sm:justify-end sm:gap-3 sm:px-6">
          <button
            type="button"
            onClick={onReset}
            disabled={!isDirty || isBusy || !formState}
            className="order-2 w-full rounded-lg border border-input bg-card px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-surface-subtle disabled:cursor-not-allowed disabled:opacity-50 sm:order-none sm:w-auto"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onSaveAsDefault}
            disabled={!isDirty || isBusy || !formState}
            className="order-1 col-span-2 w-full rounded-lg border border-primary-border bg-card px-5 py-2.5 text-sm font-semibold text-primary-emphasis transition-colors hover:bg-primary-subtle disabled:cursor-not-allowed disabled:opacity-50 sm:order-none sm:col-auto sm:w-auto"
          >
            {isSaving ? "Saving..." : "Save Preferences"}
          </button>
          <button
            type="button"
            onClick={onExport}
            disabled={isBusy || !formState}
            className="order-3 w-full rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover hover:shadow-md active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:order-none sm:w-auto"
          >
            {isExporting ? "Exporting..." : `Export ${format.toUpperCase()}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
